import { AsyncPipe, DatePipe } from '@angular/common'
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
} from '@angular/core'
import { MatIconButton } from '@angular/material/button'
import { MatIcon } from '@angular/material/icon'
import { MatSort, MatSortHeader } from '@angular/material/sort'
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
} from '@angular/material/table'
import {
  ElderBasicPaneLayoutComponent,
  ElderDataToolbarComponent,
  ElderPaneComponent,
  ElderStopEventPropagationDirective,
  ElderTableActivationDirective,
  ElderTableComponent,
  ElderTableToolbarDirective,
  IDataContext,
  IsoIntervalParsePipe,
} from '@elderbyte/ngx-starter'
import { LoggerFactory } from '@elderbyte/ts-logger'
import { ElderTranslatePipe } from '@elderbyte/ngx-starter'
import { BehaviorSubject } from 'rxjs'
import { WorkSchedule } from '../work-schedule'
import { WorkScheduleDetailComponent } from '../work-schedule-detail/work-schedule-detail.component'

@Component({
  selector: 'app-work-schedule-manager',
  templateUrl: './work-schedule-manager.component.html',
  styleUrls: ['./work-schedule-manager.component.scss'],
  imports: [
    ElderTableComponent,
    ElderTableActivationDirective,
    MatSort,
    ElderTableToolbarDirective,
    ElderDataToolbarComponent,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatSortHeader,
    MatCellDef,
    MatCell,
    MatIconButton,
    ElderStopEventPropagationDirective,
    MatIcon,
    WorkScheduleDetailComponent,
    AsyncPipe,
    DatePipe,
    ElderTranslatePipe,
    IsoIntervalParsePipe,
    ElderBasicPaneLayoutComponent,
    ElderPaneComponent,
  ],
})
export class WorkScheduleManagerComponent implements OnDestroy {
  /***************************************************************************
   *                                                                         *
   * Fields                                                                  *
   *                                                                         *
   **************************************************************************/

  private readonly log = LoggerFactory.getLogger(this.constructor.name)

  private readonly _activeSchedule$ = new BehaviorSubject<WorkSchedule>(null)

  public readonly canAdd$ = new BehaviorSubject<boolean>(true)

  @Input()
  public data: IDataContext<WorkSchedule>

  @Output()
  public create: EventEmitter<Event> = new EventEmitter<Event>()

  @Output()
  public deleteAll: EventEmitter<WorkSchedule[]> = new EventEmitter<WorkSchedule[]>()

  @Output()
  public duplicate: EventEmitter<WorkSchedule> = new EventEmitter<WorkSchedule>()

  @Output()
  public save: EventEmitter<WorkSchedule> = new EventEmitter<WorkSchedule>()

  /***************************************************************************
   *                                                                         *
   * Constructor                                                             *
   *                                                                         *
   **************************************************************************/

  constructor() {}

  /***************************************************************************
   *                                                                         *
   * Life Cycle                                                              *
   *                                                                         *
   **************************************************************************/

  public ngOnDestroy(): void {
    if (this.data) {
      this.data.close()
    }
  }

  /***************************************************************************
   *                                                                         *
   * Properties                                                              *
   *                                                                         *
   **************************************************************************/

  public get activeSchedule$() {
    return this._activeSchedule$.asObservable()
  }

  public get activeSchedule(): WorkSchedule {
    return this._activeSchedule$.getValue()
  }

  public set activeSchedule(facilitySchedule: WorkSchedule) {
    this._activeSchedule$.next(facilitySchedule)
  }

  public get canAdd(): boolean {
    return this.canAdd$.getValue()
  }

  @Input()
  public set canAdd(canAdd: boolean) {
    this.canAdd$.next(canAdd)
  }

  /***************************************************************************
   *                                                                         *
   * Public API                                                              *
   *                                                                         *
   **************************************************************************/

  public openDetail(schedule: WorkSchedule): void {
    this.activeSchedule = schedule
  }

  public onCreate(event: Event): void {
    this.create.emit(event)
  }

  public onRemoveAll(schedules: WorkSchedule[]): void {
    this.activeSchedule = null
    this.deleteAll.emit(schedules)
  }

  public onDuplicateSchedule(schedule: WorkSchedule): void {
    this.duplicate.emit(schedule)
  }

  public onScheduleChanged(schedule: WorkSchedule) {
    this.save.emit(schedule)
  }
}
